import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import ArchiveBeneficiary from '@Application/Beneficiaries/Beneficiaries/Commands/ArchiveBeneficiary';
import DeleteBeneficiary from '@Application/Beneficiaries/Beneficiaries/Commands/DeleteBeneficiary';
import archiveBeneficiary from '@Application/Beneficiaries/Beneficiaries/archiveBeneficiary';
import deleteBeneficiary from '@Application/Beneficiaries/Beneficiaries/deleteBeneficiary';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import beneficiaryToArchiveSignal, {
	closeBeneficiaryToArchive,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Signals/beneficiaryToArchiveSignal';
import beneficiaryToDeleteSignal, {
	closeBeneficiaryToDelete,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Signals/beneficiaryToDeleteSignal';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog, ConfirmDialog } from '@verclam/ui-kit';

/**
 * The two confirmations mounted by `BeneficiaryDetails` (V-16): archiving/unarchiving (reversible,
 * `ConfirmDialog`) and hard deletion (`ConfirmDeleteDialog`, retyped name). Both signals only ever carry
 * the id of the dossier currently open on that page, so the beneficiary they confirm against is read
 * straight from `beneficiariesStore.beneficiary` rather than passed in as a prop.
 */
const BeneficiariesConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const beneficiary = useSelector((state) => state.beneficiariesStore.beneficiary);
	const beneficiaryToArchiveId = useSignalValue(beneficiaryToArchiveSignal);
	const beneficiaryToDeleteId = useSignalValue(beneficiaryToDeleteSignal);

	const beneficiaryToArchive =
		beneficiary !== null && beneficiary.id === beneficiaryToArchiveId ? beneficiary : null;
	const beneficiaryToDelete =
		beneficiary !== null && beneficiary.id === beneficiaryToDeleteId ? beneficiary : null;

	const handleArchiveDialogHide = React.useCallback(() => {
		closeBeneficiaryToArchive();
	}, []);

	const handleArchiveAccept = React.useCallback(() => {
		if (beneficiaryToArchive === null) {
			return;
		}

		const nextStatus = beneficiaryToArchive.isArchived ? 'active' : 'archived';

		dispatch(
			archiveBeneficiary(
				new ArchiveBeneficiary(beneficiaryToArchive.facilityId, beneficiaryToArchive.id, nextStatus),
			),
		).catch(ignoreHandledError);
	}, [beneficiaryToArchive, dispatch]);

	const handleDeleteDialogHide = React.useCallback(() => {
		closeBeneficiaryToDelete();
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		if (beneficiaryToDelete === null) {
			return;
		}

		dispatch(
			deleteBeneficiary(new DeleteBeneficiary(beneficiaryToDelete.facilityId, beneficiaryToDelete.id)),
		)
			.then(() => navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY)))
			.catch(ignoreHandledError);
		// `acceptCallback` never triggers `onHide` on `ConfirmDeleteDialog`: close explicitly, immediately,
		// rather than waiting for the request to settle and leaving the dialog open long enough for a
		// second click.
		closeBeneficiaryToDelete();
	}, [beneficiaryToDelete, dispatch, navigate]);

	return (
		<React.Fragment>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={beneficiaryToArchive !== null}
				onHide={handleArchiveDialogHide}
				header={beneficiaryToArchive?.isArchived ? 'Désarchiver le dossier' : 'Archiver le dossier'}
				message={
					beneficiaryToArchive?.isArchived
						? 'Le dossier redevient modifiable.'
						: "Le dossier passe en lecture seule : plus d'entretien ni de document, mais tout reste consultable. L'opération est réversible."
				}
				acceptLabel={beneficiaryToArchive?.isArchived ? 'Désarchiver' : 'Archiver'}
				accept={handleArchiveAccept}
			/>
			<ConfirmDeleteDialog
				visible={beneficiaryToDelete !== null}
				onHide={handleDeleteDialogHide}
				header="Supprimer définitivement"
				message="La suppression sert à corriger une erreur : un dossier créé en double, ouvert pour la mauvaise personne. Elle est définitive."
				confirmText={beneficiaryToDelete?.fullName ?? ''}
				acceptCallback={handleDeleteAccept}
			/>
		</React.Fragment>
	);
};

export default BeneficiariesConfirmationDialogs;
